import React, { useEffect } from 'react';
import SEO from '../SEO';
import seoData from '../../seoData';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { CheckCircle, ArrowRight, Shield, Clock, Award, Users, Wrench, ThumbsUp, Phone, Zap } from 'lucide-react';
import Header from '../Header';
import Footer from '../Footer';
import PageHero from '../ui/PageHero';
import { useNavigate } from 'react-router-dom';

const WhyChooseBCS = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const coreValues = [
    {
      icon: Shield,
      title: 'Quality First',
      description: 'We use only premium, proven materials and follow rigorous application standards on every project.'
    },
    {
      icon: Clock,
      title: 'Rapid Response',
      description: '24/7 emergency service ensures your critical equipment gets back online fast.'
    },
    {
      icon: Award,
      title: 'Certified Expertise',
      description: 'Our technicians are factory-trained and certified in the latest repair technologies.'
    },
    {
      icon: Users,
      title: 'Customer Partnership',
      description: 'We work alongside your team to understand your unique challenges and deliver tailored solutions.'
    }
  ];

  const differentiators = [
    {
      title: 'No Hot Work Required',
      description: 'Our cold-applied systems eliminate fire risks and permit requirements, allowing repairs in live plant conditions.',
      benefit: 'Safer, faster repairs'
    },
    {
      title: 'In-Situ Repairs',
      description: 'Many repairs can be completed without removing equipment, saving time and labor costs.',
      benefit: 'Reduced downtime'
    },
    {
      title: 'Extended Warranties',
      description: 'We stand behind our work with comprehensive warranties backed by material manufacturers.',
      benefit: 'Peace of mind'
    },
    {
      title: 'Turnkey Solutions',
      description: 'From assessment through installation and QA/QC documentation, we handle the entire project.',
      benefit: 'One-stop service'
    }
  ];

  const credentials = [
    'CSLB License No. 1066525 | Class A & C-33',
    'DIR No. 2000002659',
    'AMPP/NACE Certified',
    'DBIDS Gov Contractor',
    'TWIC Credentialed',
    'DUNS No. 080247052'
  ];

  const stats = [
    { value: '25+', label: 'Years Experience' },
    { value: '1,000+', label: 'Projects Completed' },
    { value: '100+', label: 'Satisfied Clients' },
    { value: '24/7', label: 'Emergency Support' }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <SEO title={seoData["about/why-choose-bcs"]?.title} description={seoData["about/why-choose-bcs"]?.description} path="/about/why-choose-bcs" keywords={seoData["about/why-choose-bcs"]?.keywords || []} />
      
      <PageHero
        badge="About BCS"
        title="Why Choose BCS?"
        description="For over 25 years, BCS has been the trusted partner for industrial repair and protection. We combine expertise, quality materials, and exceptional service to deliver results you can count on."
        backgroundKey="why-choose-bcs"
        buttonText="Get in Touch"
      />

      {/* Stats Section */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 bg-blue-900 text-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {stats.map((stat, index) => (
              <div key={index}>
                <div className="text-4xl font-bold mb-2">{stat.value}</div>
                <div className="text-blue-200">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Our Core Values</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {coreValues.map((value, index) => {
              const IconComponent = value.icon;
              return (
                <Card key={index} className="text-center border-t-4 border-t-green-700">
                  <CardHeader>
                    <IconComponent className="mx-auto text-green-700 mb-2" size={40} />
                    <CardTitle className="text-lg">{value.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600 text-sm">{value.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Differentiators */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">What Sets Us Apart</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {differentiators.map((item, index) => (
              <Card key={index} className="border-l-4 border-l-blue-900">
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span>{item.title}</span>
                    <Badge className="bg-green-100 text-green-800">{item.benefit}</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600">{item.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Credentials */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-slate-900 mb-6">Credentials & Certifications</h2>
              <p className="text-gray-600 mb-8">
                BCS maintains all required licenses, certifications, and credentials to work in the most demanding 
                industrial environments, including government and military facilities.
              </p>
              <div className="grid grid-cols-2 gap-3">
                {credentials.map((credential, index) => (
                  <div key={index} className="flex items-center bg-green-50 p-3 rounded-lg">
                    <CheckCircle className="text-green-700 mr-2 flex-shrink-0" size={18} />
                    <span className="text-gray-700 text-sm">{credential}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-green-700 text-white p-8 rounded-lg">
              <ThumbsUp className="mb-4" size={48} />
              <h3 className="text-2xl font-bold mb-4">100% Satisfaction Guarantee</h3>
              <p className="text-green-100 mb-6">
                We're not satisfied until you are. Our commitment to quality means we stand behind every repair 
                and coating application we perform.
              </p>
              <Button className="bg-white text-green-700 hover:bg-gray-100" onClick={() => navigate('/about/testimonials')}>
                Read Customer Reviews <ArrowRight className="ml-2" size={16} />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-blue-900 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <Phone className="mx-auto mb-4" size={48} />
          <h2 className="text-3xl font-bold mb-4">Ready to Work with the Best?</h2>
          <p className="text-blue-100 mb-8">
            Contact us today for a free consultation and see why leading companies trust BCS.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" className="bg-white text-blue-900 hover:bg-gray-100">
              <a href="tel:9493719948" className="flex items-center">
                Call (949) 371-9948
              </a>
            </Button>
            <Button size="lg" variant="outline" className="border-white text-white hover:bg-blue-800" onClick={() => navigate('/#contact')}>
              Request Quote
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default WhyChooseBCS;
